import React from 'react';
import { useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DetailsCardDetailList, {
    SubjectProfileDetailsCardItem,
    formatDate,
    formatStringOrNumber,
} from './details-card-detail-list';
import styles from './index.module.scss';
import Badge from '@components-clean/core/Badge';
import Card from '@components-clean/core/card';
import { InfoField } from '@components-clean/core/info-field';
import Loader from '@components-clean/core/loader';
import { useTranslation } from '@hooks/index';
import { GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION } from '@queries/subject-monitoring';
import { SubjectMonitoringAPI } from '@apis/bff-api/subject-monitoring/subject-profile';

type Props = {
    studyRef: string;
    datasetId: number;
    subjectId: number;
};

const SubjectProfileDetailsSiteInformationCard = ({ studyRef, datasetId, subjectId }: Props) => {
    const { t: tCommon } = useTranslation('common');

    const { t } = useTranslation('sm', {
        keyPrefix: 'subjectProfile.siteInformation',
    });

    const getSubjectDetailsQuery = SubjectMonitoringAPI.useGetSubjectDetails(studyRef, datasetId, subjectId);
    const getSiteInformationQuery = SubjectMonitoringAPI.useConditionalGetSiteById(
        studyRef,
        datasetId,
        getSubjectDetailsQuery?.data?.parentPgid ?? null, // Null disables query while SubjectDetails are loading
    );

    const getSubjectProfileDetailsConfigQuery = useQuery(GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION, {
        variables: { studyId: studyRef },
    });

    const siteInformation = getSiteInformationQuery.data;
    const sectionConfig =
        getSubjectProfileDetailsConfigQuery.data?.subjectProfileDetailsConfiguration
            ?.subjectProfileDetailsConfigurationItem?.siteInformation;

    const isLoading =
        getSubjectDetailsQuery.isLoading ||
        getSiteInformationQuery.isLoading ||
        getSubjectProfileDetailsConfigQuery.loading;

    const isError =
        !!getSubjectDetailsQuery.error ||
        !!getSiteInformationQuery.error ||
        !!getSubjectProfileDetailsConfigQuery.error;

    const items: SubjectProfileDetailsCardItem[] = [];

    if (sectionConfig?.country) {
        items.push({
            key: 'country',
            label: t('country'),
            value: formatStringOrNumber(siteInformation?.country),
        });
    }
    if (sectionConfig?.activeDate) {
        items.push({
            key: 'active-date',
            label: t('activeDate'),
            value: formatDate(siteInformation?.activeDate),
        });
    }
    if (sectionConfig?.closedDate) {
        items.push({
            key: 'closed-date',
            label: t('closedDate'),
            value: formatDate(siteInformation?.closedDate),
        });
    }
    if (sectionConfig?.monitor) {
        items.push({
            key: 'monitor',
            label: t('monitor'),
            value: formatStringOrNumber(siteInformation?.monitor),
        });
    }
    if (sectionConfig?.investigatorName) {
        items.push({
            key: 'investigator-name',
            label: t('investigatorName'),
            value: formatStringOrNumber(siteInformation?.investigatorName),
        });
    }
    if (sectionConfig?.subjectsScreened) {
        items.push({
            key: 'subjectsScreened',
            label: t('subjectsScreened'),
            value: formatStringOrNumber(siteInformation?.subjectsScreened),
        });
    }
    if (sectionConfig?.subjectsScreenFailed) {
        items.push({
            key: 'subjectsScreenFail',
            label: t('subjectsScreenFailed'),
            value: formatStringOrNumber(siteInformation?.subjectsScreenFailed),
        });
    }
    if (sectionConfig?.subjectEnrolled) {
        items.push({
            key: 'subjectEnrolled',
            label: t('subjectEnrolled'),
            value: formatStringOrNumber(siteInformation?.subjectsEnrolled),
        });
    }
    if (sectionConfig?.subjectRandomized) {
        items.push({
            key: 'subjectRandomized',
            label: t('subjectRandomized'),
            value: formatStringOrNumber(siteInformation?.subjectsRandomized),
        });
    }
    if (sectionConfig?.subjectTxStart) {
        items.push({
            key: 'subjectTxStart',
            label: t('subjectTxStart'),
            value: formatStringOrNumber(siteInformation?.subjectsTxStart),
        });
    }
    if (sectionConfig?.subjectTxDisc) {
        items.push({
            key: 'subjectTxDisc',
            label: t('subjectTxDisc'),
            value: formatStringOrNumber(siteInformation?.subjectsTxStart),
        });
    }
    if (sectionConfig?.subjectsTxComplete) {
        items.push({
            key: 'subjectsTxComplete',
            label: t('subjectsTxComplete'),
            value: formatStringOrNumber(siteInformation?.subjectsTxComplete),
        });
    }
    if (sectionConfig?.subjectsFUStart) {
        items.push({
            key: 'subjectsFUStart',
            label: t('subjectsFUStart'),
            value: formatStringOrNumber(siteInformation?.subjectsFuStart),
        });
    }
    if (sectionConfig?.subjectsFUComplete) {
        items.push({
            key: 'subjectsFUComplete',
            label: t('subjectsFUComplete'),
            value: formatStringOrNumber(siteInformation?.subjectsFuComplete),
        });
    }
    if (sectionConfig?.numberOfAEs) {
        items.push({
            key: 'numberOfAEs',
            label: t('numberOfAEs'),
            value: formatStringOrNumber(siteInformation?.aEsAll),
        });
    }
    if (sectionConfig?.numberOfSAEs) {
        items.push({
            key: 'numberOfSAEs',
            label: t('numberOfSAEs'),
            value: formatStringOrNumber(siteInformation?.saEsAll),
        });
    }
    if (sectionConfig?.numberOfAESIs) {
        items.push({
            key: 'numberOfAESIs',
            label: t('numberOfAESIs'),
            value: formatStringOrNumber(siteInformation?.aEsSpecialInterest),
        });
    }
    if (sectionConfig?.numberOfPDs) {
        items.push({
            key: 'numberOfPDs',
            label: t('numberOfPDs'),
            value: formatStringOrNumber(siteInformation?.pDsAll),
        });
    }
    if (sectionConfig?.numberOfQueriesOpenAll) {
        items.push({
            key: 'numberOfQueriesOpenAll',
            label: t('numberOfQueriesOpenAll'),
            value: formatStringOrNumber(siteInformation?.queriesOpenAll),
        });
    }

    if (!isLoading && !isError && items.length === 0) {
        return null;
    }

    return (
        <Card
            data-testid="subject-profile-details-site-information-card"
            title={
                <div className={styles.cardTitle}>
                    <span>{sectionConfig?.label ?? 'Site Information'}</span>
                    {siteInformation?.name && (
                        <Badge variant="primary" tooltip={formatStringOrNumber(siteInformation.name)} maxWidth="30ch">
                            Site: {siteInformation.name}
                        </Badge>
                    )}
                    {sectionConfig?.status && siteInformation?.status && (
                        <Badge
                            variant="secondary"
                            tooltip={formatStringOrNumber(siteInformation.status)}
                            maxWidth="30ch"
                        >
                            {formatStringOrNumber(siteInformation.status)}
                        </Badge>
                    )}
                </div>
            }
            padding="none"
            collapsible
        >
            <div className={styles.cardIconAndDetailsListWrapper}>
                <div className={styles.cardIconWrapper}>
                    <FontAwesomeIcon icon={['fas', 'hospital']} className={styles.cardIcon} />
                </div>

                {isLoading || isError ? (
                    <div className={styles.cardLoadingStateWrapper}>
                        {isError ? (
                            <InfoField type="Error" message={tCommon('errorMessage')} />
                        ) : isLoading ? (
                            <Loader />
                        ) : null}
                    </div>
                ) : (
                    <DetailsCardDetailList items={items} />
                )}
            </div>
        </Card>
    );
};

export default SubjectProfileDetailsSiteInformationCard;
